<template>
  <el-dialog v-model="visible" title="自定义课程" width="1050px" align-center class="base-modal-wrap"
    :close-on-click-modal="false" :show-close="showClose" @close="handleClose" :label-position="'top'">

    <div v-if="isGenerating">
      <div class="loading-content">
        <el-icon class="loading-icon">
          <Loading />
        </el-icon>
        <h3 class="loading-title">正在生成课程大纲</h3>
        <p class="loading-desc">预计需要30秒...</p>
        <div class="progress-container">
          <div class="progress-bar" :style="{ width: `${generatingProgress}%` }"></div>
        </div>
        <p class="loading-percentage">{{ Math.floor(generatingProgress) }}%</p>
      </div>
    </div>

    <div class="base-modal-body" v-if="!isGenerating">
      <div class="course-generation">
        <div class="course-header text-center">
          <div class="generating-status">
            <el-tag type="info" v-if="isGenerating">
              <el-icon>
                <Loading />
              </el-icon>
              正在生成课程
            </el-tag>
          </div>
          <h3 class="course-title w-full">{{ courseTitle }}</h3>
        </div>

        <div class="course-description text-left">
          <p>{{ courseDescription }}</p>
        </div>

        <div class="course-modules" v-if="courseModules.length > 0">
          <div class="modules-grid">
            <div v-for="(module, index) in courseModules" :key="index" class="module-card">
              <h4 class="module-title">● {{ module.title }}</h4>
              <ul class="module-content">
                <li v-for="item in module.contents" :key="item" class="module-item ml-4">
                  ✧ {{ item.sub_title }}
                  <ul class="module-content mb-3">
                    <li v-for="thirdItem in item.sub_contents" class="ml-4">- {{ thirdItem }}</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="course-actions-bottom" v-if="!isGenerating">
          <el-button @click="arrangeCourse('before')" class="text-[#1a56db]">安排该课在最前</el-button>
          <el-button @click="arrangeCourse('after')" class="text-[#1a56db]">安排该课在最后</el-button>
          <el-button @click="arrangeCourse('replace')" class="text-[#1a56db]">替换已有的课程</el-button>
          <el-button type="primary" @click="arrangeCourse('random')">随机分布排课</el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { ElMessage } from "element-plus";
import { Loading } from '@element-plus/icons-vue';
import { Charpter, getCustomCourse, insertCourse } from '@/api/modules/chat';
import { useRouter } from "vue-router";

const props = defineProps({
  modelValue: Boolean,
  courseTopic: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  callBack: {
    type: Function,
    default: () => { },
  },
  arrangeCourseCallback: {
    type: Function,
    default: () => { }
  }
});

const router = useRouter()

const emits = defineEmits(['update:modelValue', 'complete']);

const visible = ref(props.modelValue);
const isGenerating = ref(false);
const courseModules = ref<Charpter[]>([]);
const courseTitle = ref('');
const courseDescription = ref('');
const generatingProgress = ref(0);
const shouldShowLoading = ref(true);
const progressTimer = ref<any>(null);

const showLoading = () => {
  if (!shouldShowLoading.value) {
    return
  }
  // 显示加载界面
  isGenerating.value = true;
  generatingProgress.value = 0;

  progressTimer.value = setInterval(() => {
    if (generatingProgress.value < 95) {
      // 非线性增长，开始快，后面慢
      const increment = generatingProgress.value < 20 ? 1 :
        generatingProgress.value < 60 ? 1 : 0.5;
      generatingProgress.value += increment;
    }
  }, 500);
}

// const courseDuration = ref('60分钟');
// 重构 courseModules 数据，使每个第三级别内容成为独立的模块
const restructureCourseModules = (originalModules: Charpter[]): Charpter[] => {
  const restructuredModules: Charpter[] = [];

  originalModules.forEach(module => {
    module.contents.forEach(content => {
      restructuredModules.push({
        title: `${module.title}`,
        contents: [{
          sub_title: `${content.sub_title}`,
          sub_contents: content.sub_contents
        }]
      });
    });
  });

  return restructuredModules;
};
// 生成课程
const generateCourse = async (topic: string) => {
  try {
    isGenerating.value = true;

    setTimeout(() => {
      showLoading()
    }, 800);

    const response = await getCustomCourse(topic);

    if (response.data && response.data.chapters) {
      courseModules.value = restructureCourseModules(response.data.chapters);
      courseTitle.value = response.data.title || topic;
      courseDescription.value = response.data.description || courseDescription.value;
      // courseDuration.value = response.data.duration || '60分钟';

      ElMessage.success('课程生成成功！');
    } else {
      ElMessage.warning('使用默认课程模板');
    }
  } catch (error) {
    ElMessage.error('课程生成失败，请重试');
    // visible.value = false;
  } finally {
    isGenerating.value = false;
  }
};

// 课程安排相关方法
const arrangeCourse = (type: any) => {
  insertCourse({
    subject: courseTitle.value,
    insert_type: type,
    chapters: courseModules.value,
  })

  ElMessage.success(`课程已安排成功`);

  visible.value = false
  if (props?.arrangeCourseCallback) {
    props?.arrangeCourseCallback()
  } else {
    setTimeout(() => {
      router.push('/plan')
    }, 500)
  }
};

// 完成课程
const handleComplete = () => {
  emits('complete', {
    topic: props.courseTopic,
    title: courseTitle.value,
    modules: courseModules.value,
    // duration: courseDuration.value
  });
  emits('update:modelValue', false);
  props?.callBack && props.callBack();
};

watch(() => props.modelValue, (val) => {
  visible.value = val;
  if (val && props.courseTopic) {
    // 当弹窗打开且有课程话题时，自动生成课程
    generateCourse(props.courseTopic);
  }
}, { immediate: true });

const handleClose = () => {
  emits('update:modelValue', false);
};

const handleCancel = () => {
  emits('update:modelValue', false);
};
</script>

<style lang="scss">
.base-modal-wrap {
  .el-dialog__header {
    font-size: 18px;
    padding-left: 12px;
    font-weight: 600;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;

    .el-dialog__headerbtn {
      font-size: 20px;
      top: 8px;
      right: 8px;
    }
  }

  .el-dialog__body {
    text-align: left;
    padding: 24px;
  }
}

.base-modal-body {}

// 课程生成页面样式
.course-generation {

  .course-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    width: 100%;

    .generating-status {
      position: absolute;
      top: 20px;
      right: 60px;
    }

    .course-title {
      font-size: 24px;
      font-weight: bold;
      color: #303133;
      margin: 0;
    }
  }

  .course-description {
    padding: 16px 0px;

    margin-bottom: 8px;

    p {
      margin: 0;
      line-height: 1.6;
      color: #606266;
    }
  }

  .course-modules {
    margin-bottom: 24px;
    height: 500px;
    overflow: auto;
    min-height: 300px;
  }

  .modules-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .module-card {
    background: white;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 16px;
    background-color: rgb(222 233 254);

    .module-title {
      font-size: 16px;
      font-weight: bold;
      color: #303133;
      margin: 0 0 12px 0;
      text-align: left;
    }

    .module-content {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .module-item {
      text-align: left;
      display: flex;
      align-items: left;
      margin-bottom: 8px;
      font-size: 14px;
      flex-direction: column;
      color: #606266;

      .diamond-icon {
        margin-right: 8px;
        color: #409eff;
      }
    }
  }

  .course-actions-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 540px;
    margin: 0 auto;
    margin-top: 36px;

    .course-duration {
      font-weight: bold;
      color: #409eff;
    }
  }
}


.loading-content {
  background-color: white;
  padding: 40px;
  text-align: center;
  width: 520px;
  margin: 0 auto;
}

.loading-icon {
  font-size: 40px;
  color: #1A56DB;
  animation: rotate 2s linear infinite;
  margin-bottom: 20px;
}

.loading-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 16px;
  color: #333;
}

.loading-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.5;
}

.progress-container {
  width: 100%;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  margin-bottom: 8px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #1A56DB, #3b82f6);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.loading-percentage {
  font-size: 14px;
  color: #1A56DB;
  font-weight: bold;
}
</style>
